import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useQuery } from '@hooks/reactQuery';

import { map } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { getSiteProfile } from '@api/manageSite';

import QueryClientService from '@utils/queryClientService';

import { ISiteProfile } from '@interfaces/cells/siteProfile';

import { EDataType } from '@enums/map';

import { refetchOptions } from '@constants/common';
import { SITE_PROFILE } from '@constants/queryKeys';

interface IUseQueryActiveSitesDataReturnType {
	prefetchSite: (id: string) => Promise<void>;
	activeSites: ISiteProfile[];
	isLoadingSites: boolean;
}

export const useQueryActiveSitesData = (): IUseQueryActiveSitesDataReturnType => {
	const queryClient = useQueryClient();

	const {
		activeSiteMarkers,
		siteIdsWithinActivePolygons,
		markerType,
	} = useAppSelector(map);

	const activeSites = useMemo(() => {
		return Array.from(new Set(activeSiteMarkers.concat(siteIdsWithinActivePolygons)));
	}, [activeSiteMarkers, siteIdsWithinActivePolygons]);

	const {
		data: sites,
		isInitialLoading,
	} = useQuery({
		queryKey: [
			SITE_PROFILE,
			activeSites,
			markerType,
		],
		queryFn: () => Promise.all(activeSites.map(id => getSiteProfile(id))),
		enabled: [markerType === EDataType.sites],
		...refetchOptions,
	});

	const prefetchSite = async (id: string) => {
		try {
			const siteQuery = QueryClientService.queryClient.getQueryCache().find([SITE_PROFILE, id, markerType]);

			if (siteQuery) {
				return;
			}

			await queryClient.prefetchQuery({
				queryKey: [SITE_PROFILE, id, markerType],
				queryFn: () => getSiteProfile(id),
			});
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
		}
	};

	return {
		activeSites: sites || [],
		isLoadingSites: isInitialLoading,
		prefetchSite,
	};
};
